import { Store } from '@ngxs/store';
import { AccountCustomizedTexts, AccountCustomizedTextsEnvironment, FALLBACK_ACCOUNT_CUSTOMIZED_TEXTS, SessionState } from '@platform-frontend/account';
import { DateParams } from '@platform-frontend/transloco';
import { TranslationId } from '@platform-frontend/util';
import { combineLatest, map, Observable, switchMap } from 'rxjs';
import { CHECKOUT_TRANSLOCO_SCOPE } from './checkout-transloco-scope';
import { AppFlow, FlowState } from './flow';
import { AccountMigration, StartupConfigurationState } from './startup';

export const CHECKOUT_ACCOUNT_CUSTOMIZED_TEXTS: (flow: AppFlow, environment?: AccountCustomizedTextsEnvironment) => AccountCustomizedTexts = (
  flow: AppFlow,
  environment?: AccountCustomizedTextsEnvironment
) => {
  switch (flow.type) {
    case 'COMMERCIAL_CHECKOUT':
      return {
        ...FALLBACK_ACCOUNT_CUSTOMIZED_TEXTS('withContinue', environment),
        signIn: {
          title: () => ({ key: CHECKOUT_TRANSLOCO_SCOPE + '.accountCustomizedTexts.signIn.title', scope: CHECKOUT_TRANSLOCO_SCOPE })
        },
        verifyEmail: {
          toContinueInstructions: {
            default: { key: CHECKOUT_TRANSLOCO_SCOPE + '.accountCustomizedTexts.verifyEmail.toContinueInstructions.default', scope: CHECKOUT_TRANSLOCO_SCOPE },
            signIn: { key: CHECKOUT_TRANSLOCO_SCOPE + '.accountCustomizedTexts.verifyEmail.toContinueInstructions.signIn', scope: CHECKOUT_TRANSLOCO_SCOPE }
          }
        }
      };
    case 'SIGN_IN':
      return {
        ...(flow.productIdentifier
          ? FALLBACK_ACCOUNT_CUSTOMIZED_TEXTS('withContinue', environment)
          : FALLBACK_ACCOUNT_CUSTOMIZED_TEXTS('pureSignIn', environment)),
        registrationComplete: { text: (store: Store) => getRegistrationCompleteTextTranslationId(store) }
      };
    default:
      return FALLBACK_ACCOUNT_CUSTOMIZED_TEXTS('withContinue', environment);
  }
};

const getRegistrationCompleteTextTranslationId = (store: Store): Observable<TranslationId> => {
  return store
    .select(StartupConfigurationState.accountMigration)
    .pipe(
      switchMap(accountMigration =>
        accountMigration
          ? getAccountMigrationRegistrationCompleteTextTranslationId(store, accountMigration)
          : FALLBACK_ACCOUNT_CUSTOMIZED_TEXTS('pureSignIn').registrationComplete.text(store)
      )
    );
};

const getAccountMigrationRegistrationCompleteTextTranslationId = (store: Store, accountMigration: AccountMigration): Observable<TranslationId> => {
  return combineLatest([store.select(FlowState.vendorId), store.select(StartupConfigurationState.vendorName), store.select(SessionState.email)]).pipe(
    map(([vendorId, vendorName, email]) => {
      const launchDate: DateParams | null =
        accountMigration.launchDate && new Date(accountMigration.launchDate) > new Date()
          ? { date: accountMigration.launchDate, options: { dateStyle: 'short' } }
          : null;
      const launchDateMode = launchDate && new Date(launchDate.date) > new Date() ? 'FUTURE' : 'PAST';
      const params = {
        emailClass: 'text-p-40 inline-flex max-w-full overflow-hidden',
        email: email ?? '',
        launchDate,
        launchDateMode,
        vendorName: vendorName ?? ''
      };

      let textKey: string;
      // TODO: This is custom code for the vendor NVV and must be removed in future, also the transloco keys!
      if (vendorId === 'd8e9993d-38fa-4d3f-b86e-f770ba6bd5f3') {
        textKey = launchDate
          ? '.accountCustomizedTexts.registrationComplete.accountMigrationText.nvv.withLaunchDate'
          : '.accountCustomizedTexts.registrationComplete.accountMigrationText.nvv.fromNow';
      }
      // End of TODO
      else {
        textKey = launchDate
          ? '.accountCustomizedTexts.registrationComplete.accountMigrationText.withLaunchDate'
          : '.accountCustomizedTexts.registrationComplete.accountMigrationText.fromNow';
      }

      return {
        key: CHECKOUT_TRANSLOCO_SCOPE + textKey,
        params,
        scope: CHECKOUT_TRANSLOCO_SCOPE
      };
    })
  );
};
